import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Upload } from 'lucide-react'

import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'

const ContactFileModal = ({ open, onClose, onSave }) => {
  const [selectedContact, setSelectedContact] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [fileName, setFileName] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [contacts, setContacts] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [isGeneralFile, setIsGeneralFile] = useState(false)

  // Search contacts dynamically
  const handleSearchContacts = useCallback(async (searchValue) => {
    if (!searchValue || searchValue.trim().length < 1) {
      setContacts([])
      return
    }

    try {
      setSearchLoading(true)
      const { data } = await api.get('/contacts', {
        params: {
          searchParam: searchValue
        }
      })
      setContacts(data.contacts || [])
    } catch (err) {
      toastError(err)
    } finally {
      setSearchLoading(false)
    }
  }, [])

  const handleSearchInputChange = (event, value) => {
    setSearchInput(value)
    handleSearchContacts(value)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedFile(file)
      setFileName(file.name)
    }
  }

  const handleSave = useCallback(async () => {
    if (!isGeneralFile && !selectedContact) {
      toast.error(i18n.t('contacts.selectContact'))
      return
    }

    if (!selectedFile) {
      toast.error(i18n.t('contacts.selectFile'))
      return
    }

    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('file', selectedFile)

      const endpoint = isGeneralFile 
        ? '/files' 
        : `/contacts/${selectedContact.id}/files`

      await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.success(i18n.t('contacts.addFileSuccess'))
      setSelectedContact(null)
      setSelectedFile(null)
      setFileName('')
      setSearchInput('')
      setIsGeneralFile(false)
      onSave()
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [selectedContact, selectedFile, isGeneralFile, onSave])

  const handleClose = () => {
    setSelectedContact(null)
    setSelectedFile(null)
    setFileName('')
    setSearchInput('')
    setContacts([])
    setIsGeneralFile(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t('contacts.addFileModal')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isGeneralFile}
                onChange={(e) => setIsGeneralFile(e.target.checked)}
              />
            }
            label={i18n.t('contacts.generalFile') || 'Arquivo Geral'}
          />

          {!isGeneralFile && (
            <Autocomplete
              value={selectedContact}
              onChange={(event, newValue) => {
                setSelectedContact(newValue)
              }}
              inputValue={searchInput}
              onInputChange={handleSearchInputChange}
              options={contacts}
              getOptionLabel={(option) => `${option.name} (${option.number})`}
              loading={searchLoading}
              noOptionsText={i18n.t('contacts.noContactsAvailable')}
              loadingText={i18n.t('common.loading')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t('contacts.selectContactLabel')}
                  placeholder={i18n.t('contacts.typeToSearchContact')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          )}

          <Box>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<Upload size={20} />}
            >
              {fileName ? fileName : i18n.t('contacts.selectFileLabel')}
              <input
                hidden
                type="file"
                onChange={handleFileSelect}
                accept="*/*"
              />
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{i18n.t('common.cancel')}</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={(!isGeneralFile && !selectedContact) || !selectedFile || loading}
        >
          {loading ? i18n.t('common.saving') : i18n.t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ContactFileModal
